import * as React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { ButtonBase, IconButton, Tooltip } from '@material-ui/core';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import PrintIcon from '@material-ui/icons/Print';
import { PERFORMANCE_LEVEL_COLORS, PERFORMANCE_LEVEL_TEXT_COLOR } from '../../common/performanceLevelColors';
import { getStudentAvatarUrl } from '../../StudentRoster/+store/studentRoster/studentRoster.utils';
import GrowthBaselineDialog from './GrowthBaselineDialog.component';
import { printGrowthMatrix } from './printGrowthMatrix';

// Current Year / Prior Year (CYPY) matrix for Domain 2A Annual Growth.
// Rows are prior-year performance, columns are current-year performance. Each
// cell is shaded by the points that transition earns: 1 point is light,
// half a point is mid-gray, 0 points is dark ("out of the dark, into the light").
//
// The rows can be swapped off prior-year STAAR onto any interim set (the swap
// icon by the title): BOY -> this report, or last year's BOY -> this year's.
// The server pairs students and writes the per-cell ids the same way the
// default matrix does, so cells, faces, names and the popup all keep working.

// Prior-year (row) x current-year (column) points, in the order of `levels`.
// Mirrors pointsGrid in functions/modules/Assessments/Assessments/Assessments.utils.js
// and the 2026 Accountability Manual Annual Growth Points (STAAR) table.
const POINTS = [
  [0, 1, 1, 1, 1, 1],
  [0, 0.5, 1, 1, 1, 1],
  [0, 0, 0.5, 1, 1, 1],
  [0, 0, 0, 0.5, 1, 1],
  [0, 0, 0, 0, 1, 1],
  [0, 0, 0, 0, 0, 1],
];

const levels = [
  { key: 'doesNotMeet', label: 'Does Not Meet', sub: 'Low', text: 'Does Not Meet Low' },
  { key: 'doesNotMeetHigh', label: 'Does Not Meet', sub: 'High', text: 'Does Not Meet High' },
  { key: 'approaches', label: 'Approaches', sub: 'Low', text: 'Approaches Low' },
  { key: 'approachesHigh', label: 'Approaches', sub: 'High', text: 'Approaches High' },
  { key: 'meets', label: 'Meets', sub: '', text: 'Meets' },
  { key: 'masters', label: 'Masters', sub: '', text: 'Masters' },
];

const POINT_THEMES = {
  1: { background: '#F1F3F4', color: '#2B2F33', muted: '#8F969C', label: '1 pt' },
  0.5: { background: '#D3D7DA', color: '#2B2F33', muted: '#6C7378', label: '½ pt' },
  0: { background: '#7B8187', color: '#FFFFFF', muted: 'rgba(255,255,255,0.75)', label: '0 pts' },
};

// Faces view limits. A cell shows up to FACES_PER_CELL faces; past that it shows
// FACES_PER_CELL - 1 faces plus a "+N" chip and the full list is one click away,
// so even a busy cell stays a fixed size. Past FACES_MAX_TOTAL tests in the
// matrix (big-district scale) the faces stop telling you anything, so the toggle
// is disabled.
const FACES_PER_CELL = 12;
const FACES_MAX_TOTAL = 2000;
// Names view: "First L." per line, two columns past NAMES_TWO_COL, then a
// "+N more" chip; the cell click still opens the full list.
const NAMES_PER_CELL = 10;
const NAMES_TWO_COL = 5;
const VIEW_STORAGE_KEY = 'cypyMatrixView';
const VIEWS = ['counts', 'faces', 'names'];

const subjectAccent = {
  reading: '#E2BF2A',
  math: '#3FA6E6',
};

const printExact = { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' };

const readStoredView = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEWS.includes(stored) ? stored : 'counts';
  } catch (e) {
    return 'counts';
  }
};

const storeView = (view) => {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch (e) {
    // localStorage unavailable (private mode); the toggle still works for this session.
  }
};

const toCount = (value) => (typeof value === 'number' ? value : Number(value) || 0);

const styles = () => ({
  card: {
    background: '#FFFFFF',
    border: '1px solid #E6E8EA',
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)',
    margin: '20px 0',
    padding: '18px 20px 14px',
    overflowX: 'auto',
    ...printExact,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: 14,
  },
  accent: {
    width: 6,
    height: 30,
    borderRadius: 3,
    flex: '0 0 auto',
    ...printExact,
  },
  titleBlock: {
    flex: 1,
  },
  titleLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: '#22272B',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: 12.5,
    color: '#7A8086',
    marginTop: 2,
  },
  subtitleError: {
    color: '#B3261E',
  },
  resetLink: {
    fontSize: 12.5,
    color: '#4A90C9',
    fontFamily: 'inherit',
    marginLeft: 6,
    padding: '0 2px',
    borderRadius: 3,
  },
  headerIcon: {
    padding: 4,
    color: '#5B6166',
    '@media print': {
      display: 'none',
    },
  },
  toggle: {
    display: 'inline-flex',
    border: '1px solid #D5D9DC',
    borderRadius: 999,
    padding: 2,
    background: '#F6F7F8',
    '@media print': {
      display: 'none',
    },
  },
  toggleOption: {
    fontSize: 12.5,
    fontWeight: 600,
    color: '#5B6166',
    padding: '4px 12px',
    borderRadius: 999,
    fontFamily: 'inherit',
  },
  toggleOptionActive: {
    background: '#FFFFFF',
    color: '#22272B',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.15)',
  },
  toggleOptionDisabled: {
    opacity: 0.45,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '26px 120px repeat(6, minmax(84px, 1fr))',
    columnGap: '3px',
    rowGap: '3px',
    minWidth: 680,
  },
  axisTop: {
    gridColumn: '3 / span 6',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#4A90C9',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    paddingBottom: 4,
  },
  axisLeft: {
    gridColumn: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    color: '#4A90C9',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  axisLeftText: {
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    whiteSpace: 'nowrap',
    maxHeight: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dotsH: {
    flex: 1,
    height: 4,
    backgroundImage: 'radial-gradient(circle, #8CC3EC 1.6px, transparent 2px)',
    backgroundSize: '9px 4px',
    backgroundRepeat: 'repeat-x',
    backgroundPosition: 'center',
    ...printExact,
  },
  dotsV: {
    flex: 1,
    width: 4,
    backgroundImage: 'radial-gradient(circle, #8CC3EC 1.6px, transparent 2px)',
    backgroundSize: '4px 9px',
    backgroundRepeat: 'repeat-y',
    backgroundPosition: 'center',
    ...printExact,
  },
  levelPill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: PERFORMANCE_LEVEL_TEXT_COLOR,
    fontSize: 12.5,
    fontWeight: 600,
    lineHeight: 1.15,
    borderRadius: 8,
    padding: '6px 6px',
    ...printExact,
  },
  colPill: {
    marginBottom: 3,
  },
  rowPill: {
    marginRight: 5,
  },
  levelSub: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.9,
    marginTop: 1,
  },
  cell: {
    position: 'relative',
    minHeight: 62,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...printExact,
  },
  cellButton: {
    width: '100%',
    height: '100%',
    minHeight: 'inherit',
    borderRadius: 6,
    transition: 'box-shadow 120ms ease, transform 120ms ease',
    '&:hover, &:focus-visible': {
      boxShadow: '0 0 0 2px #4A90C9, 0 4px 10px rgba(16, 24, 40, 0.18)',
      transform: 'translateY(-1px)',
    },
  },
  cellInner: {
    width: '100%',
    height: '100%',
    minHeight: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  countEmpty: {
    fontSize: 16,
    fontWeight: 400,
  },
  pointTag: {
    position: 'absolute',
    top: 4,
    right: 7,
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '0.02em',
    pointerEvents: 'none',
  },
  faces: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignContent: 'center',
    gap: '4px',
    padding: '18px 6px 8px',
    width: '100%',
    boxSizing: 'border-box',
  },
  face: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
    background: '#C9CED2',
    flex: '0 0 auto',
  },
  moreChip: {
    minWidth: 28,
    height: 28,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    background: 'rgba(255,255,255,0.92)',
    color: '#3A3F44',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
    flex: '0 0 auto',
  },
  names: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '18px 8px 8px',
    textAlign: 'left',
  },
  namesCount: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 2,
    opacity: 0.8,
  },
  namesList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    fontSize: 11.5,
    lineHeight: 1.3,
  },
  namesListTwoCol: {
    columnCount: 2,
    columnGap: 8,
  },
  namesItem: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    breakInside: 'avoid',
  },
  namesMore: {
    fontSize: 11,
    fontWeight: 700,
    marginTop: 3,
    opacity: 0.85,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: 14,
    paddingLeft: 152,
    minWidth: 680,
    boxSizing: 'border-box',
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    fontSize: 12,
    color: '#5B6166',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  swatch: {
    width: 22,
    height: 14,
    borderRadius: 3,
    border: '1px solid rgba(0,0,0,0.08)',
    ...printExact,
  },
  quote: {
    fontSize: 12,
    fontStyle: 'italic',
    color: '#8A9096',
  },
  coverage: {
    fontSize: 12,
    color: '#5B6166',
  },
});

const studentName = (student) =>
  student ? [student.firstName, student.lastName].filter(Boolean).join(' ') : 'Student not on roster';

const studentShortName = (student) => {
  if (!student) return 'Not on roster';
  const first = student.firstName || '';
  const last = student.lastName ? `${student.lastName.substring(0, 1)}.` : '';
  return [first || 'N/A', last].filter(Boolean).join(' ');
};

const sortByName = (ids, roster) =>
  [...ids].sort((a, b) => {
    const sa = (roster && roster[a]) || {};
    const sb = (roster && roster[b]) || {};
    const last = (sa.lastName || '').localeCompare(sb.lastName || '');
    return last !== 0 ? last : (sa.firstName || '').localeCompare(sb.firstName || '');
  });

const Faces = ({ classes, ids, total, roster, avatars }) => {
  const unique = sortByName(Array.from(new Set(ids)), roster);
  const overflow = total > FACES_PER_CELL;
  const shown = overflow ? unique.slice(0, FACES_PER_CELL - 1) : unique.slice(0, FACES_PER_CELL);
  const hidden = total - shown.length;
  return (
    <div className={classes.faces}>
      {shown.map((id) => {
        const student = roster && roster[id];
        const src =
          (student && student.photoUrl && student.photoUrl.small) || getStudentAvatarUrl(student, avatars) || null;
        return src ? (
          <img key={id} src={src} alt={studentName(student)} title={studentName(student)} className={classes.face} />
        ) : (
          <span key={id} title={studentName(student)} className={classes.face} />
        );
      })}
      {hidden > 0 && <span className={classes.moreChip}>+{hidden}</span>}
    </div>
  );
};

Faces.propTypes = {
  classes: PropTypes.object.isRequired,
  ids: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  roster: PropTypes.object,
  avatars: PropTypes.object,
};

const Names = ({ classes, ids, total, roster }) => {
  const unique = sortByName(Array.from(new Set(ids)), roster);
  const shown = unique.slice(0, NAMES_PER_CELL);
  const hidden = total - shown.length;
  const twoCols = shown.length > NAMES_TWO_COL;
  return (
    <div className={classes.names}>
      <div className={classes.namesCount}>{total}</div>
      <ul className={`${classes.namesList} ${twoCols ? classes.namesListTwoCol : ''}`}>
        {shown.map((id) => {
          const student = roster && roster[id];
          return (
            <li key={id} className={classes.namesItem} title={studentName(student)}>
              {studentShortName(student)}
            </li>
          );
        })}
      </ul>
      {hidden > 0 && <div className={classes.namesMore}>{`and ${hidden} more…`}</div>}
    </div>
  );
};

Names.propTypes = {
  classes: PropTypes.object.isRequired,
  ids: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  roster: PropTypes.object,
};

const MatrixCell = ({ classes, count, points, onClick, ariaLabel, gridRow, gridColumn, faces }) => {
  const theme = POINT_THEMES[points];
  const content = (
    <div className={classes.cellInner}>
      <span className={classes.pointTag} style={{ color: theme.muted }}>
        {theme.label}
      </span>
      {faces && count > 0 ? (
        faces
      ) : count > 0 ? (
        <span className={classes.count}>{count}</span>
      ) : (
        <span className={classes.countEmpty} style={{ color: theme.muted }}>
          –
        </span>
      )}
    </div>
  );
  return (
    <div
      className={classes.cell}
      style={{ background: theme.background, color: theme.color, gridRow, gridColumn }}
      title={faces ? undefined : ariaLabel}
    >
      {onClick && count > 0 ? (
        <ButtonBase className={classes.cellButton} onClick={onClick} aria-label={ariaLabel} focusRipple>
          {content}
        </ButtonBase>
      ) : (
        content
      )}
    </div>
  );
};

MatrixCell.propTypes = {
  classes: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  points: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
  gridRow: PropTypes.number.isRequired,
  gridColumn: PropTypes.number.isRequired,
  faces: PropTypes.node,
};

const LevelPill = ({ classes, level, className, style }) => (
  <div
    className={`${classes.levelPill} ${className}`}
    style={{ background: PERFORMANCE_LEVEL_COLORS[level.key], ...style }}
  >
    <span>{level.label}</span>
    {level.sub ? <span className={classes.levelSub}>{level.sub}</span> : null}
  </div>
);

LevelPill.propTypes = {
  classes: PropTypes.object.isRequired,
  level: PropTypes.object.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
};

const sumLevels = (levelCounts) => Object.values(levelCounts || {}).reduce((sum, n) => sum + toCount(n), 0);

const filtersLabel = (filters) => {
  if (!filters) return '';
  const describe = (label, value) => {
    if (!value || value === 'all') return null;
    const list = Array.isArray(value) ? value.filter((v) => v && v !== 'all') : [value];
    return list.length ? `${label}: ${list.join(', ')}` : null;
  };
  return [
    describe('Grades', filters.grades),
    describe('Teachers', filters.teachers),
    describe('Student groups', filters.studentGroups),
    describe('Tags', filters.tags),
  ]
    .filter((x) => x)
    .join(' · ');
};

const D2AAnnualGrowthTable = ({
  subjectName,
  classes,
  subtableLabel,
  subtable,
  studentsListPath,
  handleGetStudentsList,
  loadMatrixStudents,
  profile,
  roster,
  avatars,
  growthMatrix,
  reportRequest,
  campusName,
  campusNumber,
  assessmentList,
  onSelectBaseline,
  onResetBaseline,
}) => {
  const [view, setView] = React.useState(readStoredView);
  const [cellStudents, setCellStudents] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  // Swapped rows (from the server) win over the report's own prior-year rows.
  const swappedTables = growthMatrix && growthMatrix.data && growthMatrix.data.tables;
  const swapped = swappedTables ? swappedTables[subjectName] : null;
  const baseline = growthMatrix ? growthMatrix.baseline : null;
  const swapLoading = !!(growthMatrix && growthMatrix.loading);
  const swapError = growthMatrix && !growthMatrix.loading ? growthMatrix.error : null;

  const table = swapped ? swapped.annualGrowthTable : subtable && subtable.count && subtable.count.annualGrowthTable;
  const activePath = swapped ? `growthMatrix/${subjectName}` : studentsListPath;

  // Swapping needs the report's own request and a single campus; district and
  // campus-group runs keep the icon hidden.
  const canSwap = !!(reportRequest && onSelectBaseline && campusNumber && String(campusNumber).length === 9);

  let totalTests = 0;
  if (table) {
    levels.forEach((prior) =>
      levels.forEach((current) => {
        totalTests += toCount(table[prior.key] && table[prior.key][current.key]);
      })
    );
  }
  const peopleAvailable =
    !!table && !!activePath && !!loadMatrixStudents && !!profile && !!roster && totalTests <= FACES_MAX_TOTAL;
  const showFaces = peopleAvailable && view === 'faces';
  const showNames = peopleAvailable && view === 'names';
  const showPeople = showFaces || showNames;

  const loadStudents = React.useCallback(() => {
    if (!activePath || !loadMatrixStudents || !profile) return Promise.resolve({});
    return Promise.resolve(loadMatrixStudents(profile.organizationId, `${profile.uid}/${activePath}`)).then(
      (data) => data || {}
    );
  }, [activePath, loadMatrixStudents, profile && profile.uid, profile && profile.organizationId]);

  // Re-read the cell ids whenever the rows change under them (new report run,
  // baseline swapped or reset).
  const rowsVersion = swapped ? JSON.stringify(baseline) : 'default';
  React.useEffect(() => {
    if (!showPeople) return undefined;
    let cancelled = false;
    setCellStudents(null);
    loadStudents()
      .then((data) => {
        if (!cancelled) setCellStudents(data);
      })
      .catch(() => {
        if (!cancelled) setCellStudents({});
      });
    return () => {
      cancelled = true;
    };
  }, [showPeople, subtable, activePath, rowsVersion, loadStudents]);

  if (!table) return null;

  const accent = subjectAccent[subjectName] || '#9AA0A5';

  const chooseView = (next) => {
    setView(next);
    storeView(next);
  };

  const makeClickHandler = (prior, current) =>
    activePath && handleGetStudentsList && profile
      ? () =>
          handleGetStudentsList(
            profile.organizationId,
            `${profile.uid}/${activePath}/${prior.key}/${current.key}`,
            `${swapped ? baseline.name || 'Baseline' : 'Prior'}: ${prior.text}`,
            `Current: ${current.text}`
          )
      : null;

  const idsAt = (source, prior, current) => {
    const node = source && source[prior.key] && source[prior.key][current.key];
    if (!node) return [];
    return (Array.isArray(node) ? node : Object.values(node)).filter((id) => id != null);
  };
  const idsFor = (prior, current) => idsAt(cellStudents, prior, current);

  const baselineLabel = baseline
    ? `${baseline.name || 'Baseline'}${baseline.date ? ` (${moment(baseline.date).format('MMM D, YYYY')})` : ''}`
    : 'Prior Year';
  const rowAxisLabel = swapped ? baseline.name || 'Baseline' : 'Prior Year';

  const handleSelectBaseline = (picked) => {
    setDialogOpen(false);
    if (!picked) {
      if (onResetBaseline) onResetBaseline();
      return;
    }
    onSelectBaseline(reportRequest, picked);
  };

  const handlePrint = () => {
    // Open the tab inside the click so the popup blocker allows it, then fill
    // it once the cell ids are in hand.
    const win = window.open('', '_blank');
    if (!win) return;
    const print = (ids) => {
      const names = {};
      levels.forEach((prior) => {
        names[prior.key] = {};
        levels.forEach((current) => {
          names[prior.key][current.key] = sortByName(
            Array.from(new Set(idsAt(ids, prior, current))),
            roster
          ).map((id) => studentShortName(roster && roster[id]));
        });
      });
      let currentOnly = null;
      let baselineOnly = null;
      if (swapped) {
        currentOnly = {};
        levels.forEach((level) => {
          const node = ids && ids.currentOnly && ids.currentOnly[level.key];
          const list = node ? (Array.isArray(node) ? node : Object.values(node)).filter((id) => id != null) : [];
          currentOnly[level.key] = sortByName(Array.from(new Set(list)), roster).map((id) =>
            studentShortName(roster && roster[id])
          );
        });
        const bos = growthMatrix.data.baselineOnlyStudents && growthMatrix.data.baselineOnlyStudents[subjectName];
        if (growthMatrix.data.baselineOnlyAvailable && bos) {
          baselineOnly = {};
          levels.forEach((level) => {
            baselineOnly[level.key] = (bos[level.key] || [])
              .map((s) => studentShortName(s))
              .sort((a, b) => a.localeCompare(b));
          });
        }
      }
      printGrowthMatrix(
        {
          title: `${subtableLabel} — Annual Growth`,
          campusName: campusName || '',
          rowsLabel: swapped ? baselineLabel : 'Prior Year STAAR',
          columnsLabel: 'Current Year',
          filtersLabel: filtersLabel(reportRequest && reportRequest.filters),
          levelLabels: levels.reduce((o, l) => {
            o[l.key] = l.text;
            return o;
          }, {}),
          counts: table,
          names,
          currentOnly,
          baselineOnly,
          footnote: swapped ? '' : 'Rows are each student’s prior-year STAAR, as paired by the A-F report.',
        },
        win
      );
    };
    if (cellStudents && showPeople) {
      print(cellStudents);
    } else {
      loadStudents()
        .then(print)
        .catch(() => print({}));
    }
  };

  const toggleOption = (key, label) => (
    <ButtonBase
      key={key}
      className={`${classes.toggleOption} ${
        view === key && (key === 'counts' || peopleAvailable) ? classes.toggleOptionActive : ''
      } ${key !== 'counts' && !peopleAvailable ? classes.toggleOptionDisabled : ''}`}
      onClick={() => chooseView(key)}
      disabled={key !== 'counts' && !peopleAvailable}
      aria-pressed={view === key}
    >
      {label}
    </ButtonBase>
  );

  const viewToggle = (
    <div className={classes.toggle} role="group" aria-label="Matrix view">
      {toggleOption('counts', 'Counts')}
      {toggleOption('faces', 'Faces')}
      {toggleOption('names', 'Names')}
    </div>
  );

  let subtitle = 'Current Year / Prior Year matrix. Click a cell to see the students.';
  let subtitleClass = classes.subtitle;
  if (swapLoading) {
    subtitle = `Loading rows from ${baselineLabel}…`;
  } else if (swapError) {
    subtitle = `Could not load rows from ${baselineLabel}: ${swapError}`;
    subtitleClass = `${classes.subtitle} ${classes.subtitleError}`;
  } else if (swapped && !growthMatrix.data.baselineTestCount) {
    subtitle = `No students found in ${baselineLabel} for this campus and year, so every row is empty.`;
    subtitleClass = `${classes.subtitle} ${classes.subtitleError}`;
  } else if (swapped) {
    subtitle = `Rows: ${baselineLabel}. Columns: this report. Click a cell to see the students.`;
  }

  const currentOnlyTotal = swapped ? sumLevels(swapped.currentOnly) : 0;
  const baselineOnlyTotal = swapped ? sumLevels(swapped.baselineOnly) : 0;

  return (
    <div className={classes.card} id={`d2aAnnualGrowth_${subjectName}`}>
      <div className={classes.header}>
        <div className={classes.accent} style={{ background: accent }} />
        <div className={classes.titleBlock}>
          <div className={classes.titleLine}>
            <span className={classes.title}>{subtableLabel} Annual Growth</span>
            {canSwap && (
              <Tooltip title="Compare from a different assessment">
                <IconButton
                  className={classes.headerIcon}
                  onClick={() => setDialogOpen(true)}
                  disabled={swapLoading}
                  aria-label="Change the assessment the rows come from"
                >
                  <SwapVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={subtitleClass}>
            {subtitle}
            {(swapped || swapError) && !swapLoading && onResetBaseline && (
              <ButtonBase className={classes.resetLink} onClick={onResetBaseline}>
                reset to Prior Year STAAR
              </ButtonBase>
            )}
          </div>
        </div>
        {peopleAvailable ? (
          viewToggle
        ) : (
          <Tooltip
            title={
              totalTests > FACES_MAX_TOTAL
                ? `Faces and names are for campus-sized groups (up to ${FACES_MAX_TOTAL} tests)`
                : 'Faces and names need the student roster'
            }
          >
            <span>{viewToggle}</span>
          </Tooltip>
        )}
        {peopleAvailable && (
          <Tooltip title="Print or save as PDF with student names">
            <IconButton className={classes.headerIcon} onClick={handlePrint} aria-label="Print with student names">
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className={classes.grid}>
        <div className={classes.axisTop} style={{ gridRow: 1 }}>
          <span className={classes.dotsH} />
          <span>Current Year</span>
          <span className={classes.dotsH} />
        </div>

        {levels.map((level, colIndex) => (
          <LevelPill
            key={`col_${level.key}`}
            classes={classes}
            level={level}
            className={classes.colPill}
            style={{ gridRow: 2, gridColumn: colIndex + 3 }}
          />
        ))}

        <div className={classes.axisLeft} style={{ gridRow: '3 / span 6' }}>
          <span className={classes.dotsV} />
          <span className={classes.axisLeftText} title={rowAxisLabel}>
            {rowAxisLabel}
          </span>
          <span className={classes.dotsV} />
        </div>

        {levels.map((prior, rowIndex) => (
          <React.Fragment key={`row_${prior.key}`}>
            <LevelPill
              classes={classes}
              level={prior}
              className={classes.rowPill}
              style={{ gridRow: rowIndex + 3, gridColumn: 2 }}
            />
            {levels.map((current, colIndex) => {
              const points = POINTS[rowIndex][colIndex];
              const count = toCount(table[prior.key] && table[prior.key][current.key]);
              let people = null;
              if (showPeople && cellStudents) {
                people = showFaces ? (
                  <Faces
                    classes={classes}
                    ids={idsFor(prior, current)}
                    total={count}
                    roster={roster}
                    avatars={avatars}
                  />
                ) : (
                  <Names classes={classes} ids={idsFor(prior, current)} total={count} roster={roster} />
                );
              }
              return (
                <MatrixCell
                  key={`cell_${prior.key}_${current.key}`}
                  classes={classes}
                  count={count}
                  points={points}
                  faces={people}
                  onClick={makeClickHandler(prior, current)}
                  gridRow={rowIndex + 3}
                  gridColumn={colIndex + 3}
                  ariaLabel={`${rowAxisLabel} ${prior.text}, current ${current.text}: ${count} tests, ${POINT_THEMES[points].label}`}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className={classes.footer}>
        <div className={classes.legend}>
          {[1, 0.5, 0].map((points) => (
            <span key={points} className={classes.legendItem}>
              <span className={classes.swatch} style={{ background: POINT_THEMES[points].background }} />
              {points === 1 ? '1 point' : points === 0.5 ? '½ point' : '0 points'}
            </span>
          ))}
        </div>
        {swapped ? (
          <div className={classes.coverage}>
            {`${swapped.matched || 0} students on both`}
            {currentOnlyTotal > 0 && ` · ${currentOnlyTotal} tested now, not in ${baseline.name || 'baseline'}`}
            {growthMatrix.data.baselineOnlyAvailable &&
              baselineOnlyTotal > 0 &&
              ` · ${baselineOnlyTotal} in ${baseline.name || 'baseline'}, not tested now`}
            {!growthMatrix.data.baselineOnlyAvailable &&
              growthMatrix.data.filtered &&
              ' · missing-now list is off while filters are active'}
          </div>
        ) : (
          <div className={classes.quote}>“Out of the dark, into the light.”</div>
        )}
      </div>

      {canSwap && (
        <GrowthBaselineDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSelect={handleSelectBaseline}
          assessmentList={assessmentList}
          reportSchoolYear={Number(reportRequest.schoolYear)}
          currentAssessmentIds={(reportRequest.assessmentsInfo || []).map((a) => a.id)}
          selectedBaseline={baseline}
        />
      )}
    </div>
  );
};

D2AAnnualGrowthTable.propTypes = {
  subjectName: PropTypes.string,
  subtable: PropTypes.object,
  classes: PropTypes.any,
  subtableLabel: PropTypes.string.isRequired,
  studentsListPath: PropTypes.string,
  handleGetStudentsList: PropTypes.func,
  loadMatrixStudents: PropTypes.func,
  rowComponents: PropTypes.array,
  profile: PropTypes.object,
  roster: PropTypes.object,
  avatars: PropTypes.object,
  growthMatrix: PropTypes.object,
  reportRequest: PropTypes.object,
  campusName: PropTypes.string,
  campusNumber: PropTypes.string,
  assessmentList: PropTypes.array,
  onSelectBaseline: PropTypes.func,
  onResetBaseline: PropTypes.func,
};

export default withStyles(styles)(D2AAnnualGrowthTable);
